import React, { useState, useEffect } from 'react'
import './MyOrders.css'
import { Link, useNavigate } from 'react-router-dom'

// Status colour mapping
const ORDER_STATUS = {
    pending: { label: 'Pending', class: 'mo-badge--pending' },
    processing: { label: 'Processing', class: 'mo-badge--processing' },
    delivered: { label: 'Delivered', class: 'mo-badge--delivered' },
    cancelled: { label: 'Cancelled', class: 'mo-badge--cancelled' },
}

const PAYMENT_STATUS = {
    pending: { label: 'Payment Pending', class: 'mo-badge--pay-pending' },
    paid: { label: 'Paid', class: 'mo-badge--paid' },
    failed: { label: 'Payment Failed', class: 'mo-badge--failed' },
}

const MyOrders = () => {

    const navigate  = useNavigate()
    const [orders,   setOrders] = useState([])
    const [loading,  setLoading] = useState(true)
    const [error,    setError] = useState('')
    const [expanded, setExpanded] = useState(null)   // orderId of expanded card
    const [waNumber, setWaNumber] = useState('')

    // Redirect if not logged in
    useEffect(() => {
        if (!localStorage.getItem('auth-token')) {
            navigate('/login')
        }
    }, [navigate])

    // Fetch customer's orders
    useEffect(() => {
        fetch(`${process.env.REACT_APP_API_URL}/my-orders`, {
            headers: { 'auth-token': localStorage.getItem('auth-token') }
        })
            .then(r => r.json())
            .then(d => {
                if (d.success) setOrders(d.orders)
                else setError('Failed to load orders.')
            })
            .catch(() => setError('Unable to connect. Please try again.'))
            .finally(() => setLoading(false))
    }, [])

    // Fetch WhatsApp number for payment slip link
    useEffect(() => {
        fetch(`${process.env.REACT_APP_API_URL}/site-settings`)
            .then(r => r.json())
            .then(d => {
                if (d.success && d.settings.whatsappNumber)
                    setWaNumber(d.settings.whatsappNumber)
            })
            .catch(console.error)
    }, [])

    const formatDate = (date) =>
        new Date(date).toLocaleDateString('en-CA', {
            year: 'numeric', month: 'long', day: 'numeric'
        })

    const waLink = (orderId) => {
        if (!waNumber) return null
        const msg = `Hi! My Order ID is *${orderId}*. Please find my payment slip attached.`
        return `https://wa.me/${waNumber}?text=${encodeURIComponent(msg)}`
    }

    const toggleExpand = (orderId) => {
        setExpanded(prev => prev === orderId ? null : orderId)
    }

    // Loading
    if (loading) {
        return (
            <div className="mo-page">
                <div className="mo-loading">
                    <div className="mo-spinner" />
                    <span>Loading your orders...</span>
                </div>
            </div>
        )
    }

    // Error
    if (error) {
        return (
            <div className="mo-page">
                <div className="mo-error">
                    <p>{error}</p>
                    <Link to="/" className="mo-shop-btn">Back to Home</Link>
                </div>
            </div>
        )
    }

    // Empty
    if (orders.length === 0) {
        return (
            <div className="mo-page">
                <div className="mo-header">
                    <h1>My Orders</h1>
                </div>
                <div className="mo-empty">
                    <div className="mo-empty-icon">🛍️</div>
                    <p className="mo-empty-title">No orders yet</p>
                    <p className="mo-empty-sub">When you place an order it will appear here.</p>
                    <Link to="/shop-products" className="mo-shop-btn">Start Shopping</Link>
                </div>
            </div>
        )
    }

    // Orders list
    return (
        <div className="mo-page">
            <div className="mo-header">
                <h1>My Orders</h1>
                <p className="mo-count">{orders.length} order{orders.length !== 1 ? 's' : ''}</p>
            </div>

            <div className="mo-list">
                {orders.map(order => {
                    const isOpen = expanded === order.orderId
                    const orderStatus = ORDER_STATUS[order.status] || { label: order.status, class: '' }
                    const payStatus = PAYMENT_STATUS[order.paymentStatus] || { label: order.paymentStatus, class: '' }
                    const wa = waLink(order.orderId)

                    return (
                        <div key={order.orderId} className={`mo-card ${isOpen ? 'mo-card--open' : ''}`}>

                            {/* ── Card header — always visible ── */}
                            <div className="mo-card-header" onClick={() => toggleExpand(order.orderId)}>
                                <div className="mo-card-header-left">
                                    <p className="mo-order-id">{order.orderId}</p>
                                    <p className="mo-order-date">{formatDate(order.date)}</p>
                                </div>
                                <div className="mo-card-header-center">
                                    <span className={`mo-badge ${orderStatus.class}`}>{orderStatus.label}</span>
                                    <span className={`mo-badge ${payStatus.class}`}>{payStatus.label}</span>
                                </div>
                                <div className="mo-card-header-right">
                                    <p className="mo-order-total">Rs. {order.totalAmount.toLocaleString()}</p>
                                    <span className="mo-expand-icon">{isOpen ? '▲' : '▼'}</span>
                                </div>
                            </div>

                            {/* ── Expanded detail ── */}
                            {isOpen && (
                                <div className="mo-card-body">

                                    {/* Items */}
                                    <div className="mo-items-section">
                                        <p className="mo-section-title">Items Ordered</p>
                                        {order.items.map((item, i) => (
                                            <div key={i} className="mo-item">
                                                <img
                                                    src={item.image}
                                                    alt={item.name}
                                                    className="mo-item-img"
                                                />
                                                <div className="mo-item-info">
                                                    <p className="mo-item-name">{item.name}</p>
                                                    <div className="mo-item-tags">
                                                        {item.selectedSize && <span className="mo-item-tag">Size: {item.selectedSize}</span>}
                                                        {item.selectedColor && <span className="mo-item-tag">{item.selectedColor}</span>}
                                                        <span className="mo-item-tag">Qty: {item.quantity}</span>
                                                    </div>
                                                </div>
                                                <p className="mo-item-price">
                                                    Rs. {(item.price * item.quantity).toLocaleString()}
                                                </p>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Order summary + shipping */}
                                    <div className="mo-detail-grid">
                                        <div className="mo-detail-block">
                                            <p className="mo-section-title">Shipping Address</p>
                                            <p className="mo-detail-line">{order.shippingAddress.fullName}</p>
                                            <p className="mo-detail-line">{order.shippingAddress.addressLine}</p>
                                            <p className="mo-detail-line">{order.shippingAddress.city}, {order.shippingAddress.postalCode}</p>
                                            <p className="mo-detail-line">📞 {order.contactNumber}</p>
                                        </div>

                                        <div className="mo-detail-block">
                                            <p className="mo-section-title">Order Summary</p>
                                            {(() => {
                                                const itemsSubtotal = order.items.reduce(
                                                    (sum, item) => sum + (item.price * item.quantity), 0
                                                )
                                                const shippingPaid = order.totalAmount - itemsSubtotal
                                                return (
                                                    <>
                                                        <div className="mo-summary-row">
                                                            <span>Subtotal</span>
                                                            <span>Rs. {itemsSubtotal.toLocaleString()}</span>
                                                        </div>
                                                        <div className="mo-summary-row">
                                                            <span>Shipping</span>
                                                            <span>Rs. {shippingPaid.toLocaleString()}</span>
                                                        </div>
                                                        <div className="mo-summary-row mo-summary-row--total">
                                                            <span>Total</span>
                                                            <span>Rs. {order.totalAmount.toLocaleString()}</span>
                                                        </div>
                                                    </>
                                                )
                                            })()}
                                        </div>
                                    </div>

                                    {/* WhatsApp link — shown only if payment is still pending */}
                                    {order.paymentStatus === 'pending' && wa && (
                                        <div className="mo-wa-wrap">
                                            <p className="mo-wa-hint">
                                                Payment not confirmed yet — send your payment slip to complete your order.
                                            </p>
                                            <a href={wa} target="_blank" rel="noreferrer" className="mo-wa-btn">
                                                <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                                                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                                                </svg>
                                                Send Payment Slip
                                            </a>
                                        </div>
                                    )}

                                </div>
                            )}
                        </div>
                    )
                })}
            </div>
        </div>
    )
}

export default MyOrders